/**
 * Guard Lookup - Search for guards/supervisors by name or employee ID
 * Displays guard assignment details and their assigned supervisor
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles, Button, Input, Text, Avatar, Card, Spinner } from '@fluentui/react-components';
import {
  SearchRegular,
  PersonRegular,
  LocationRegular,
  CallRegular,
  MailRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ArrowSyncRegular,
} from '@fluentui/react-icons';
import { ApiClient, newToast, getEmployeeImageUrl } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  pageHeader: {
    marginBottom: '8px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  pageTitleLight: {
    fontWeight: '400',
    color: '#605e5c',
  },
  helperText: {
    fontSize: '14px',
    color: '#605e5c',
    marginBottom: '24px',
    display: 'block',
  },
  searchContainer: {
    display: 'flex',
    gap: '12px',
    marginBottom: '32px',
  },
  searchInput: {
    flex: 1,
    '& input': {
      fontSize: '16px',
      padding: '12px 16px',
    },
  },
  searchButton: {
    minWidth: '120px',
    height: '44px',
    fontSize: '16px',
    backgroundColor: '#0d7377',
    ':hover': {
      backgroundColor: '#0a5c5f',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '60px',
  },
  noResultsContainer: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#605e5c',
  },
  noResultsIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: 0.5,
  },
  resultCard: {
    marginBottom: '24px',
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  guardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    marginBottom: '20px',
  },
  guardAvatar: {
    width: '80px',
    height: '80px',
    fontSize: '32px',
    backgroundColor: '#0d7377',
  },
  guardInfo: {
    flex: 1,
  },
  guardName: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
  },
  guardRole: {
    fontSize: '16px',
    color: '#605e5c',
    marginLeft: '8px',
    fontWeight: '400',
  },
  employeeId: {
    fontSize: '14px',
    color: '#605e5c',
    marginBottom: '8px',
  },
  clockStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
  },
  clockedIn: {
    color: '#0d7377',
  },
  clockedOut: {
    color: '#a4262c',
  },
  detailsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    borderTop: '1px solid #e1dfdd',
    paddingTop: '16px',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
  },
  detailLabel: {
    width: '100px',
    fontSize: '14px',
    color: '#605e5c',
    fontWeight: '500',
  },
  detailValue: {
    flex: 1,
    fontSize: '14px',
    color: '#323130',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#0d7377',
    marginBottom: '16px',
  },
  supervisorCard: {
    padding: '20px',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    border: '1px solid #e1dfdd',
  },
  supervisorHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  supervisorInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  supervisorAvatar: {
    width: '56px',
    height: '56px',
    backgroundColor: '#0d7377',
  },
  supervisorName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
  },
  supervisorId: {
    fontSize: '13px',
    color: '#605e5c',
    marginLeft: '8px',
  },
  supervisorRole: {
    fontSize: '14px',
    color: '#605e5c',
  },
  supervisorActions: {
    display: 'flex',
    gap: '12px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  supervisorContact: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '4px',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#323130',
  },
  contactIcon: {
    color: '#605e5c',
    fontSize: '16px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '24px',
    padding: '12px 0',
    borderTop: '1px solid #e1dfdd',
  },
  zoneIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#605e5c',
  },
  lastUpdated: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#605e5c',
  },
  refreshIcon: {
    cursor: 'pointer',
    ':hover': {
      color: '#0d7377',
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '80px 20px',
    color: '#605e5c',
    gap: '12px',
  },
  emptyIcon: {
    fontSize: '64px',
    opacity: 0.4,
    color: '#605e5c',
  },
});

interface GuardAssignment {
  id: number;
  client_id: number;
  site_id: number;
  shift_id: number;
  employee_id: number;
  status: string;
  created_at: string;
  client_name: string;
  zone_name: string;
  site_name: string;
  shift_name: string;
  start_time: string;
  end_time: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  face_url?: string;
}

interface SupervisorAssignment {
  id: number;
  employee_id: number;
  zone_id: number;
  shift_id: number;
  start_date: string;
  end_date?: string;
}

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  employee_number?: string;
}

interface Zone {
  id: number;
  name: string;
  zoneid?: number;
}

interface SearchResult {
  guard: GuardAssignment;
  supervisor: {
    name: string;
    employeeId: string;
    phone: string;
    email: string;
  } | null;
}

export const GuardLookupFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [guardAssignments, setGuardAssignments] = useState<GuardAssignment[]>([]);
  const [supervisorAssignments, setSupervisorAssignments] = useState<SupervisorAssignment[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [guardsRes, supervisorsRes, employeesRes, zonesRes] = await Promise.all([
        ApiClient.get('/get-assign-guards'),
        ApiClient.get('/get-assign-supervisors'),
        ApiClient.get('/get-employee-data'),
        ApiClient.get('/get-zones'),
      ]);

      if (guardsRes.data.status === 'success') {
        setGuardAssignments(guardsRes.data.data || []);
      }
      if (supervisorsRes.data.status === 'success') {
        setSupervisorAssignments(supervisorsRes.data.data || []);
      }
      if (employeesRes.data.status === 'success') {
        setEmployees(employeesRes.data.data || []);
      }
      if (zonesRes.data.status === 'success') {
        setZones(zonesRes.data.data || []);
      }

      setDataLoaded(true);
    } catch (error) {
      newToast('error', getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  const getZoneIdByName = (zoneName: string): number | null => {
    const zone = zones.find(z => z.name === zoneName);
    return zone?.id || zone?.zoneid || null;
  };

  const getSupervisorForZone = (zoneName: string) => {
    const zoneId = getZoneIdByName(zoneName);
    if (!zoneId) {
      return null;
    }

    const supervisorAssignment = supervisorAssignments.find(
      sa => sa.zone_id === zoneId && (!sa.end_date || new Date(sa.end_date) > new Date())
    );

    if (!supervisorAssignment) {
      return null;
    }

    const supervisorEmployee = employees.find(e => e.id === supervisorAssignment.employee_id);
    if (!supervisorEmployee) {
      return null;
    }

    return {
      name: `${supervisorEmployee.first_name} ${supervisorEmployee.last_name}`,
      employeeId:
        supervisorEmployee.employee_number || `E${String(supervisorEmployee.id).padStart(6, '0')}`,
      phone: supervisorEmployee.phone || '',
      email: supervisorEmployee.email || '',
    };
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      newToast('error', 'Please enter a name or employee ID');
      return;
    }

    setSearching(true);
    setHasSearched(true);

    const query = searchQuery.toLowerCase().trim();

    const foundGuard = guardAssignments.find(g => {
      const fullName = `${g.first_name} ${g.last_name}`.toLowerCase();
      const employeeIdStr = `e${String(g.employee_id).padStart(6, '0')}`.toLowerCase();
      const employeeIdNum = String(g.employee_id);

      return (
        fullName.includes(query) ||
        g.first_name?.toLowerCase().includes(query) ||
        g.last_name?.toLowerCase().includes(query) ||
        employeeIdStr.includes(query) ||
        employeeIdNum.includes(query)
      );
    });

    if (foundGuard) {
      const supervisor = getSupervisorForZone(foundGuard.zone_name);
      setSearchResult({ guard: foundGuard, supervisor });
      setLastUpdated(new Date());
    } else {
      setSearchResult(null);
    }

    setSearching(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleRefresh = () => {
    if (searchResult) {
      handleSearch();
    }
  };

  const handleCallSupervisor = () => {
    if (searchResult?.supervisor?.phone) {
      window.location.href = `tel:${searchResult.supervisor.phone}`;
    }
  };

  const handleSendMemo = () => {
    if (searchResult?.supervisor) {
      navigate('/account/memo/compose');
    }
  };

  const formatTime = (time: string) => {
    if (!time) {
      return '';
    }
    const parts = time.split(':');
    const hours = parts[0] || '0';
    const minutes = parts[1] || '00';
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getRelativeTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) {
      return 'Just now';
    }
    if (diffMins === 1) {
      return '1 minute ago';
    }
    if (diffMins < 60) {
      return `${diffMins} minutes ago`;
    }

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) {
      return '1 hour ago';
    }
    return `${diffHours} hours ago`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Guard Lookup</Text>
      </div>
      <Text className={styles.helperText}>
        Enter the name or employee ID of the guard or supervisor you are looking for
      </Text>

      <div className={styles.searchContainer}>
        <Input
          className={styles.searchInput}
          placeholder="Search guard by name or employee ID..."
          value={searchQuery}
          onChange={(e, data) => setSearchQuery(data.value)}
          onKeyPress={handleKeyPress}
          contentBefore={<SearchRegular />}
          size="large"
          disabled={!dataLoaded}
        />
        <Button
          className={styles.searchButton}
          appearance="primary"
          onClick={handleSearch}
          disabled={searching || !dataLoaded}
        >
          {searching ? <Spinner size="tiny" /> : 'Search'}
        </Button>
      </div>

      {!dataLoaded && (
        <div className={styles.loadingContainer}>
          <Spinner size="large" label="Loading data..." />
        </div>
      )}

      {dataLoaded && !hasSearched && (
        <div className={styles.emptyState}>
          <SearchRegular className={styles.emptyIcon} />
          <Text size={400} weight="semibold" block>
            Search for a guard
          </Text>
          <Text size={300} style={{ color: '#605e5c' }}>
            Enter a name or employee ID to view assignment details
          </Text>
        </div>
      )}

      {dataLoaded && hasSearched && !searchResult && !searching && (
        <div className={styles.noResultsContainer}>
          <PersonRegular className={styles.noResultsIcon} />
          <Text size={400} weight="semibold" block>
            No guard found
          </Text>
          <Text size={300}>Try searching with a different name or employee ID</Text>
        </div>
      )}

      {searchResult && (
        <>
          <Card className={styles.resultCard}>
            <div className={styles.guardHeader}>
              <Avatar
                className={styles.guardAvatar}
                name={`${searchResult.guard.first_name} ${searchResult.guard.last_name}`}
                image={
                  searchResult.guard.face_url
                    ? { src: getEmployeeImageUrl(searchResult.guard.face_url) }
                    : undefined
                }
                size={72}
              />
              <div className={styles.guardInfo}>
                <Text className={styles.guardName}>
                  {searchResult.guard.first_name} {searchResult.guard.last_name}
                  <span className={styles.guardRole}>Guard</span>
                </Text>
                <Text className={styles.employeeId}>
                  E{String(searchResult.guard.employee_id).padStart(6, '0')}
                </Text>
                <div
                  className={`${styles.clockStatus} ${searchResult.guard.status === 'active' ? styles.clockedIn : styles.clockedOut}`}
                >
                  {searchResult.guard.status === 'active' ? (
                    <>
                      <CheckmarkCircleRegular />
                      <span>Active Assignment</span>
                    </>
                  ) : (
                    <>
                      <DismissCircleRegular />
                      <span>Inactive</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.detailsGrid}>
              <div className={styles.detailRow}>
                <Text className={styles.detailLabel}>Zone</Text>
                <Text className={styles.detailValue}>{searchResult.guard.zone_name || '-'}</Text>
              </div>
              <div className={styles.detailRow}>
                <Text className={styles.detailLabel}>Client</Text>
                <Text className={styles.detailValue}>{searchResult.guard.client_name || '-'}</Text>
              </div>
              <div className={styles.detailRow}>
                <Text className={styles.detailLabel}>Client Site</Text>
                <Text className={styles.detailValue}>
                  {searchResult.guard.site_name || '-'}
                  {searchResult.guard.shift_name && (
                    <span style={{ color: '#605e5c' }}>
                      {' '}
                      ({formatTime(searchResult.guard.start_time)} -{' '}
                      {formatTime(searchResult.guard.end_time)} shift)
                    </span>
                  )}
                </Text>
              </div>
            </div>
          </Card>

          <Text className={styles.sectionTitle}>Assigned Supervisor</Text>

          {searchResult.supervisor ? (
            <div className={styles.supervisorCard}>
              <div className={styles.supervisorHeader}>
                <div className={styles.supervisorInfo}>
                  <Avatar
                    className={styles.supervisorAvatar}
                    name={searchResult.supervisor.name}
                    size={56}
                  />
                  <div>
                    <Text className={styles.supervisorName}>
                      {searchResult.supervisor.name}
                      <span className={styles.supervisorId}>
                        {searchResult.supervisor.employeeId}
                      </span>
                    </Text>
                    <Text className={styles.supervisorRole}>Zone Supervisor</Text>
                  </div>
                </div>
                <div className={styles.supervisorActions}>
                  <Button
                    appearance="outline"
                    icon={<CallRegular />}
                    onClick={handleCallSupervisor}
                    disabled={!searchResult.supervisor.phone}
                  >
                    Call Supervisor
                  </Button>
                  <Button appearance="outline" icon={<MailRegular />} onClick={handleSendMemo}>
                    Send Memo
                  </Button>
                </div>
              </div>

              <div className={styles.supervisorContact}>
                {searchResult.supervisor.phone && (
                  <div className={styles.contactItem}>
                    <CallRegular className={styles.contactIcon} />
                    <span>{searchResult.supervisor.phone}</span>
                  </div>
                )}
                {searchResult.supervisor.email && (
                  <div className={styles.contactItem}>
                    <MailRegular className={styles.contactIcon} />
                    <span>{searchResult.supervisor.email}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.supervisorCard}>
              <Text style={{ color: '#605e5c', textAlign: 'center', padding: '20px' }}>
                No supervisor assigned to this zone
              </Text>
            </div>
          )}

          <div className={styles.footer}>
            <div className={styles.zoneIndicator}>
              <LocationRegular />
              <span>{searchResult.guard.zone_name || 'Unknown Zone'}</span>
            </div>
            {lastUpdated && (
              <div className={styles.lastUpdated}>
                <span>Last updated {getRelativeTime(lastUpdated)}</span>
                <ArrowSyncRegular className={styles.refreshIcon} onClick={handleRefresh} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GuardLookupFluentUI;
